import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { resolveCollectionName } from "../services/attendanceCollections";
import { getVisibleAttendanceBranches } from "../config/appBranches";

function dateFromFirestoreField(v) {
  if (v == null || v === "") return null;
  if (typeof v?.toDate === "function") {
    const t = v.toDate();
    return isNaN(t?.getTime?.()) ? null : t;
  }
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === "string" || typeof v === "number") {
    const t = new Date(v);
    return isNaN(t.getTime()) ? null : t;
  }
  return null;
}

function firstVal(d, keys) {
  for (const k of keys) {
    if (d[k] !== undefined && d[k] !== null && d[k] !== "") return d[k];
  }
  return null;
}

/**
 * Loads employee rows from each visible branch’s Firestore employees collection.
 * @param {{ role?: string, allowedBranches?: unknown }} storedUser — e.g. JSON.parse(localStorage.user)
 */
export async function loadEmployeesAcrossBranches(storedUser) {
  const visible = getVisibleAttendanceBranches(storedUser);
  const out = [];
  for (const br of visible) {
    const colName = await resolveCollectionName(br.employeeCandidates || []);
    if (!colName) continue;
    const snap = await getDocs(collection(db, colName));
    snap.forEach((docSnap) => {
      const d = docSnap.data() || {};
      const doj = dateFromFirestoreField(
        firstVal(d, ["DOJ", "doj", "dateOfJoining", "DateOfJoining", "joiningDate", "JoiningDate"])
      );
      const leavingDate = dateFromFirestoreField(
        firstVal(d, [
          "leavingDate",
          "relievingDate",
          "exitDate",
          "leftDate",
          "LeftDate",
          "dateOfLeaving",
          "DateOfLeaving",
        ])
      );
      out.push({
        branchLabel: br.label,
        collection: colName,
        docId: docSnap.id,
        employeeId: String(d.employeeId ?? d.EmployeeId ?? "").trim(),
        name: String(d.EmployeeName ?? d.name ?? "").trim(),
        phone: String(d.phone ?? "").trim(),
        status: String(d.Status ?? d.status ?? "").trim(),
        department: String(d.department ?? d.Department ?? "").trim(),
        employmentType: String(d.EmployementType ?? d.employmentType ?? "").trim(),
        monthlySalary: d.monthlySalary ?? d.MonthlySalary ?? "",
        companyId: String(d.CompanyId ?? d.companyId ?? "").trim(),
        doj,
        dojYmd: doj ? doj.toISOString().slice(0, 10) : "",
        expectedStartTime: String(d.expectedStartTime ?? d.ExpectedStartTime ?? "").trim(),
        bankAccount: String(
          d.bankAccount ?? d.BankAccount ?? d.accountNo ?? d.AccountNo ?? d.bank_account ?? ""
        ).trim(),
        bankIfsc: String(d.ifsc ?? d.IFSC ?? d.bankIfsc ?? d.BankIfsc ?? "").trim(),
        bankName: String(d.bankName ?? d.BankName ?? "").trim(),
        netPay: d.netPay ?? d.NetPay ?? d.netSalary ?? "",
        leavingDate,
        leavingYmd: leavingDate ? leavingDate.toISOString().slice(0, 10) : "",
      });
    });
  }
  return out;
}

/** @param {Awaited<ReturnType<typeof loadEmployeesAcrossBranches>>} rows */
export function summarizeHeadcount(rows) {
  /** @type {Record<string, { branch: string, working: number, onLeave: number, left: number, other: number, total: number }>} */
  const byBranch = {};
  for (const r of rows) {
    const b = r.branchLabel || "—";
    if (!byBranch[b]) {
      byBranch[b] = { branch: b, working: 0, onLeave: 0, left: 0, other: 0, total: 0 };
    }
    const s = String(r.status || "").toLowerCase().replace(/\s+/g, " ");
    byBranch[b].total += 1;
    if (s === "working") byBranch[b].working += 1;
    else if (s === "on leave") byBranch[b].onLeave += 1;
    else if (s === "left") byBranch[b].left += 1;
    else byBranch[b].other += 1;
  }
  return Object.values(byBranch).sort((a, b) => a.branch.localeCompare(b.branch));
}

/** Headcount grouped by branch + department (non-empty department only counted in its bucket). */
export function summarizeDepartmentHeadcount(rows) {
  /** @type {Record<string, { branch: string, department: string, count: number }>} */
  const map = {};
  for (const r of rows) {
    const b = r.branchLabel || "—";
    const dept = String(r.department || "").trim() || "—";
    const key = `${b}||${dept}`;
    if (!map[key]) map[key] = { branch: b, department: dept, count: 0 };
    map[key].count += 1;
  }
  return Object.values(map).sort((a, b) =>
    a.branch !== b.branch ? a.branch.localeCompare(b.branch) : a.department.localeCompare(b.department)
  );
}

/** @param {Date} start @param {Date} end — inclusive calendar days */
export function filterJoinersInRange(rows, start, end) {
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime();
  return rows.filter((r) => {
    if (!r.doj) return false;
    const t = r.doj.getTime();
    return t >= s && t <= e;
  });
}

export function filterLeaversInRange(rows, start, end) {
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime();
  return rows.filter((r) => {
    const st = String(r.status || "")
      .trim()
      .toLowerCase();
    if (st !== "left") return false;
    if (r.leavingDate) {
      const t = r.leavingDate.getTime();
      return t >= s && t <= e;
    }
    return false;
  });
}

/** monthIndex 0–11 */
export function filterAnniversaryByDojMonth(rows, monthIndex) {
  return rows.filter((r) => r.doj && r.doj.getMonth() === monthIndex);
}

export function rowsToCsv(rows, columns) {
  const esc = (v) => {
    const s = String(v ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => esc(c.header ?? c.key)).join(",");
  const lines = rows.map((row) => columns.map((c) => esc(row[c.key])).join(","));
  return [header, ...lines].join("\r\n");
}
